import { LimitsWire, TrackingDataWire } from "../../../flatbuffers/mannequin";
import { GameEventKeyFrame, GameEventSerializer } from "../game-event";

import {
  TrackingFrame,
  TrackingFrameSerializer,
  TrackingFrameWireLegacy
} from "./frame";
import { Builder } from "flatbuffers";
import { Version } from "../../../../version";

export interface TrackingData {
  version: string;
  frames: TrackingFrame[];
  limits: [number, number];
  eventKeyFrame?: GameEventKeyFrame;
}

export interface TrackingDataWireLegacy {
  version: string;
  frames: TrackingFrameWireLegacy[];
  limits: [number, number];
  eventKeyFrame?: GameEventKeyFrame;
}

export class TrackingDataSerializer {
  static serializeBinary(data: TrackingData, builder: Builder): number | undefined {
    const frameOffsets = data.frames
      .map(f => TrackingFrameSerializer.serializeBinary(f, builder))
      .filter((offset): offset is number => typeof offset === "number");

    if (frameOffsets.length !== data.frames.length) {
      console.error("failure to properly serialise frames");
      return undefined;
    }
    const frames = TrackingDataWire.createFramesVector(builder, frameOffsets);

    const keyFrameEvents = data.eventKeyFrame
      ? Object.values(data.eventKeyFrame).flatMap(events => events)
      : undefined;
    const keyFrameOffsets = keyFrameEvents
      ?.map(e => GameEventSerializer.serializeBinary(e, builder))
      .filter((offset): offset is number => typeof offset === "number");
    if (keyFrameOffsets?.length !== keyFrameEvents?.length) {
      console.error("failure to properly serialise key frame");
      return undefined;
    }
    const keyFrame = keyFrameOffsets
      ? TrackingDataWire.createEventKeyFrameVector(builder, keyFrameOffsets)
      : undefined;

    const version = builder.createString(Version.get());

    TrackingDataWire.startTrackingDataWire(builder);
    TrackingDataWire.addFrames(builder, frames);
    TrackingDataWire.addLimits(
      builder,
      LimitsWire.createLimitsWire(
        builder, data.limits[0], data.limits[1]
      )
    );
    if (keyFrame) {
      TrackingDataWire.addEventKeyFrame(builder, keyFrame);
    }

    TrackingDataWire.addVersion(builder, version);

    return TrackingDataWire.endTrackingDataWire(builder);
  }

  static deserializeBinary(wire: TrackingDataWire): TrackingData | undefined {
    const framesUnpacked: TrackingFrame[] = [];
    for (let i = 0; i < wire.framesLength(); i++) {
      const frame = TrackingFrameSerializer.deserializeBinary(wire.frames(i) || undefined);
      if (!frame) {
        console.error("error deserializing frame");
        return undefined;
      }
      framesUnpacked.push(frame);
    }

    const limits = wire.limits();
    const version = wire.version();
    if (!limits || !version) {
      console.error("error deserializing limits or version");
      return undefined;
    }

    const keyFrame: GameEventKeyFrame = {};
    for (let i = 0; i < wire.eventKeyFrameLength(); i++) {
      const event = GameEventSerializer.deserializeBinary(wire.eventKeyFrame(i) || undefined);
      if (!event) {
        console.error("error deserializing game event key frame");
        return undefined;
      }

      if (!keyFrame[event.type]) {
        keyFrame[event.type] = [];
      }

      keyFrame[event.type]?.push(event);
    }

    return {
      version: version.toString(),
      frames: framesUnpacked,
      limits: [limits.start(), limits.end()],
      eventKeyFrame: keyFrame,
    }
  }

  static deserializeLegacy(wire: TrackingDataWireLegacy): TrackingData | undefined {
    if (!wire || !wire.frames) return undefined;

    const framesUnpacked: TrackingFrame[] = [];
    for (const frame of wire.frames) {
      framesUnpacked.push(TrackingFrameSerializer.deserializeLegacy(frame));
    }

    return {
      version: wire.version,
      frames: framesUnpacked,
      limits: wire.limits,
      eventKeyFrame: wire.eventKeyFrame,
    }
  }
}
